import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    Session,
    SessionActivity,
    SessionBookmark,
    SessionAnalytics,
    SessionTemplate,
    SessionStats,
    CreateSessionData,
    UpdateSessionData,
    CreateActivityData,
    SessionType,
} from '@/services/sessionApi';

// State interface
export interface SessionState {
    // Session data
    sessions: Session[];
    currentSession: Session | null;
    activeSessions: Session[];
    sessionStats: SessionStats | null;

    // Activities and bookmarks
    currentSessionActivities: SessionActivity[];
    currentSessionBookmarks: SessionBookmark[];

    // Templates
    sessionTemplates: SessionTemplate[];

    // Analytics
    sessionAnalytics: SessionAnalytics[];

    // UI state
    loading: {
        sessions: boolean;
        currentSession: boolean;
        activities: boolean;
        bookmarks: boolean;
        templates: boolean;
        analytics: boolean;
        stats: boolean;
    };

    // Error state
    error: {
        sessions: string | null;
        currentSession: string | null;
        activities: string | null;
        bookmarks: string | null;
        templates: string | null;
        analytics: string | null;
        stats: string | null;
    };
}

// Initial state
const initialState: SessionState = {
    sessions: [],
    currentSession: null,
    activeSessions: [],
    sessionStats: null,
    currentSessionActivities: [],
    currentSessionBookmarks: [],
    sessionTemplates: [],
    sessionAnalytics: [],
    loading: {
        sessions: false,
        currentSession: false,
        activities: false,
        bookmarks: false,
        templates: false,
        analytics: false,
        stats: false,
    },
    error: {
        sessions: null,
        currentSession: null,
        activities: null,
        bookmarks: null,
        templates: null,
        analytics: null,
        stats: null,
    },
};

// Slice
const sessionSlice = createSlice({
    name: 'session',
    initialState,
    reducers: {
        // Session CRUD Actions
        fetchSessionsRequest: (state) => {
            state.loading.sessions = true;
            state.error.sessions = null;
        },
        fetchSessionsSuccess: (state, action: PayloadAction<Session[]>) => {
            state.loading.sessions = false;
            state.sessions = action.payload;
            state.error.sessions = null;
        },
        fetchSessionsFailure: (state, action: PayloadAction<string>) => {
            state.loading.sessions = false;
            state.error.sessions = action.payload;
        },

        fetchSessionByIdRequest: (state, action: PayloadAction<number>) => {
            state.loading.currentSession = true;
            state.error.currentSession = null;
        },
        fetchSessionByIdSuccess: (state, action: PayloadAction<Session>) => {
            state.loading.currentSession = false;
            state.currentSession = action.payload;
            state.error.currentSession = null;
        },
        fetchSessionByIdFailure: (state, action: PayloadAction<string>) => {
            state.loading.currentSession = false;
            state.error.currentSession = action.payload;
        },

        createSessionRequest: (state, action: PayloadAction<CreateSessionData>) => {
            state.loading.currentSession = true;
            state.error.currentSession = null;
        },
        createSessionSuccess: (state, action: PayloadAction<Session>) => {
            state.loading.currentSession = false;
            state.currentSession = action.payload;
            state.sessions.unshift(action.payload);
            state.error.currentSession = null;
        },
        createSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading.currentSession = false;
            state.error.currentSession = action.payload;
        },

        updateSessionRequest: (state, action: PayloadAction<{ sessionId: number; data: UpdateSessionData }>) => {
            state.loading.currentSession = true;
            state.error.currentSession = null;
        },
        updateSessionSuccess: (state, action: PayloadAction<Session>) => {
            state.loading.currentSession = false;
            const updatedSession = action.payload;

            // Update current session if it matches
            if (state.currentSession?.id === updatedSession.id) {
                state.currentSession = updatedSession;
            }

            // Update session in sessions array
            const index = state.sessions.findIndex((s) => s.id === updatedSession.id);
            if (index !== -1) {
                state.sessions[index] = updatedSession;
            }

            state.error.currentSession = null;
        },
        updateSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading.currentSession = false;
            state.error.currentSession = action.payload;
        },

        deleteSessionRequest: (state, action: PayloadAction<number>) => {
            state.loading.sessions = true;
            state.error.sessions = null;
        },
        deleteSessionSuccess: (state, action: PayloadAction<number>) => {
            state.loading.sessions = false;
            const sessionId = action.payload;
            state.sessions = state.sessions.filter((s) => s.id !== sessionId);
            if (state.currentSession?.id === sessionId) {
                state.currentSession = null;
            }
            state.error.sessions = null;
        },
        deleteSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading.sessions = false;
            state.error.sessions = action.payload;
        },

        // Session Control Actions
        pauseSessionRequest: (state, action: PayloadAction<number>) => {
            state.loading.currentSession = true;
            state.error.currentSession = null;
        },
        pauseSessionSuccess: (state, action: PayloadAction<{ sessionId: number; status: string }>) => {
            state.loading.currentSession = false;
            const { sessionId } = action.payload;

            if (state.currentSession?.id === sessionId) {
                state.currentSession = { ...state.currentSession, status: 'PAUSED' };
            }

            const index = state.sessions.findIndex((s) => s.id === sessionId);
            if (index !== -1) {
                state.sessions[index] = { ...state.sessions[index], status: 'PAUSED' };
            }

            state.error.currentSession = null;
        },
        pauseSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading.currentSession = false;
            state.error.currentSession = action.payload;
        },

        resumeSessionRequest: (state, action: PayloadAction<number>) => {
            state.loading.currentSession = true;
            state.error.currentSession = null;
        },
        resumeSessionSuccess: (state, action: PayloadAction<{ sessionId: number; status: string }>) => {
            state.loading.currentSession = false;
            const { sessionId } = action.payload;

            if (state.currentSession?.id === sessionId) {
                state.currentSession = { ...state.currentSession, status: 'ACTIVE' };
            }

            const index = state.sessions.findIndex((s) => s.id === sessionId);
            if (index !== -1) {
                state.sessions[index] = { ...state.sessions[index], status: 'ACTIVE' };
            }

            state.error.currentSession = null;
        },
        resumeSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading.currentSession = false;
            state.error.currentSession = action.payload;
        },

        completeSessionRequest: (state, action: PayloadAction<{ sessionId: number; finalScore?: number }>) => {
            state.loading.currentSession = true;
            state.error.currentSession = null;
        },
        completeSessionSuccess: (state, action: PayloadAction<Session>) => {
            state.loading.currentSession = false;
            const completedSession = action.payload;

            if (state.currentSession?.id === completedSession.id) {
                state.currentSession = completedSession;
            }

            const index = state.sessions.findIndex((s) => s.id === completedSession.id);
            if (index !== -1) {
                state.sessions[index] = completedSession;
            }

            state.error.currentSession = null;
        },
        completeSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading.currentSession = false;
            state.error.currentSession = action.payload;
        },

        abandonSessionRequest: (state, action: PayloadAction<number>) => {
            state.loading.currentSession = true;
            state.error.currentSession = null;
        },
        abandonSessionSuccess: (state, action: PayloadAction<{ sessionId: number; status: string }>) => {
            state.loading.currentSession = false;
            const { sessionId } = action.payload;

            if (state.currentSession?.id === sessionId) {
                state.currentSession = { ...state.currentSession, status: 'ABANDONED' };
            }

            const index = state.sessions.findIndex((s) => s.id === sessionId);
            if (index !== -1) {
                state.sessions[index] = { ...state.sessions[index], status: 'ABANDONED' };
            }

            state.error.currentSession = null;
        },
        abandonSessionFailure: (state, action: PayloadAction<string>) => {
            state.loading.currentSession = false;
            state.error.currentSession = action.payload;
        },

        // Active Sessions
        fetchActiveSessionsRequest: (state) => {
            state.loading.sessions = true;
            state.error.sessions = null;
        },
        fetchActiveSessionsSuccess: (state, action: PayloadAction<Session[]>) => {
            state.loading.sessions = false;
            state.activeSessions = action.payload;
            state.error.sessions = null;
        },
        fetchActiveSessionsFailure: (state, action: PayloadAction<string>) => {
            state.loading.sessions = false;
            state.error.sessions = action.payload;
        },

        // Session Activities
        fetchSessionActivitiesRequest: (state, action: PayloadAction<number>) => {
            state.loading.activities = true;
            state.error.activities = null;
        },
        fetchSessionActivitiesSuccess: (state, action: PayloadAction<SessionActivity[]>) => {
            state.loading.activities = false;
            state.currentSessionActivities = action.payload;
            state.error.activities = null;
        },
        fetchSessionActivitiesFailure: (state, action: PayloadAction<string>) => {
            state.loading.activities = false;
            state.error.activities = action.payload;
        },

        createSessionActivityRequest: (
            state,
            action: PayloadAction<{ sessionId: number; data: CreateActivityData }>,
        ) => {
            state.loading.activities = true;
            state.error.activities = null;
        },
        createSessionActivitySuccess: (state, action: PayloadAction<SessionActivity>) => {
            state.loading.activities = false;
            state.currentSessionActivities.push(action.payload);
            state.error.activities = null;
        },
        createSessionActivityFailure: (state, action: PayloadAction<string>) => {
            state.loading.activities = false;
            state.error.activities = action.payload;
        },

        // Session Bookmarks
        fetchSessionBookmarksRequest: (state, action: PayloadAction<number>) => {
            state.loading.bookmarks = true;
            state.error.bookmarks = null;
        },
        fetchSessionBookmarksSuccess: (state, action: PayloadAction<SessionBookmark[]>) => {
            state.loading.bookmarks = false;
            state.currentSessionBookmarks = action.payload;
            state.error.bookmarks = null;
        },
        fetchSessionBookmarksFailure: (state, action: PayloadAction<string>) => {
            state.loading.bookmarks = false;
            state.error.bookmarks = action.payload;
        },

        createSessionBookmarkRequest: (
            state,
            action: PayloadAction<{
                sessionId: number;
                data: {
                    title: string;
                    description?: string;
                    activity_sequence: number;
                    bookmark_data?: Record<string, any>;
                };
            }>,
        ) => {
            state.loading.bookmarks = true;
            state.error.bookmarks = null;
        },
        createSessionBookmarkSuccess: (state, action: PayloadAction<SessionBookmark>) => {
            state.loading.bookmarks = false;
            state.currentSessionBookmarks.push(action.payload);
            state.error.bookmarks = null;
        },
        createSessionBookmarkFailure: (state, action: PayloadAction<string>) => {
            state.loading.bookmarks = false;
            state.error.bookmarks = action.payload;
        },

        // Session Templates
        fetchSessionTemplatesRequest: (state) => {
            state.loading.templates = true;
            state.error.templates = null;
        },
        fetchSessionTemplatesSuccess: (state, action: PayloadAction<SessionTemplate[]>) => {
            state.loading.templates = false;
            state.sessionTemplates = action.payload;
            state.error.templates = null;
        },
        fetchSessionTemplatesFailure: (state, action: PayloadAction<string>) => {
            state.loading.templates = false;
            state.error.templates = action.payload;
        },

        createSessionFromTemplateRequest: (
            state,
            action: PayloadAction<{
                templateId: number;
                data: {
                    title?: string;
                    session_data?: Record<string, any>;
                    content_type?: number;
                    object_id?: number;
                };
            }>,
        ) => {
            state.loading.currentSession = true;
            state.error.currentSession = null;
        },
        createSessionFromTemplateSuccess: (state, action: PayloadAction<Session>) => {
            state.loading.currentSession = false;
            state.currentSession = action.payload;
            state.sessions.unshift(action.payload);
            state.error.currentSession = null;
        },
        createSessionFromTemplateFailure: (state, action: PayloadAction<string>) => {
            state.loading.currentSession = false;
            state.error.currentSession = action.payload;
        },

        // Session Stats
        fetchSessionStatsRequest: (state) => {
            state.loading.stats = true;
            state.error.stats = null;
        },
        fetchSessionStatsSuccess: (state, action: PayloadAction<SessionStats>) => {
            state.loading.stats = false;
            state.sessionStats = action.payload;
            state.error.stats = null;
        },
        fetchSessionStatsFailure: (state, action: PayloadAction<string>) => {
            state.loading.stats = false;
            state.error.stats = action.payload;
        },

        // Filter Actions
        fetchSessionsByTypeRequest: (state, action: PayloadAction<keyof SessionType>) => {
            state.loading.sessions = true;
            state.error.sessions = null;
        },
        fetchSessionsByTypeSuccess: (state, action: PayloadAction<Session[]>) => {
            state.loading.sessions = false;
            state.sessions = action.payload;
            state.error.sessions = null;
        },
        fetchSessionsByTypeFailure: (state, action: PayloadAction<string>) => {
            state.loading.sessions = false;
            state.error.sessions = action.payload;
        },

        fetchSessionsByDateRangeRequest: (state, action: PayloadAction<{ startDate: string; endDate: string }>) => {
            state.loading.sessions = true;
            state.error.sessions = null;
        },
        fetchSessionsByDateRangeSuccess: (state, action: PayloadAction<Session[]>) => {
            state.loading.sessions = false;
            state.sessions = action.payload;
            state.error.sessions = null;
        },
        fetchSessionsByDateRangeFailure: (state, action: PayloadAction<string>) => {
            state.loading.sessions = false;
            state.error.sessions = action.payload;
        },

        // Analytics
        fetchSessionAnalyticsRequest: (state) => {
            state.loading.analytics = true;
            state.error.analytics = null;
        },
        fetchSessionAnalyticsSuccess: (state, action: PayloadAction<SessionAnalytics[]>) => {
            state.loading.analytics = false;
            state.sessionAnalytics = action.payload;
            state.error.analytics = null;
        },
        fetchSessionAnalyticsFailure: (state, action: PayloadAction<string>) => {
            state.loading.analytics = false;
            state.error.analytics = action.payload;
        },

        // Utility Actions
        clearCurrentSession: (state) => {
            state.currentSession = null;
            state.currentSessionActivities = [];
            state.currentSessionBookmarks = [];
        },

        clearSessionError: (state, action: PayloadAction<keyof SessionState['error']>) => {
            state.error[action.payload] = null;
        },
    },
});

export const {
    // Session CRUD
    fetchSessionsRequest,
    fetchSessionsSuccess,
    fetchSessionsFailure,
    fetchSessionByIdRequest,
    fetchSessionByIdSuccess,
    fetchSessionByIdFailure,
    createSessionRequest,
    createSessionSuccess,
    createSessionFailure,
    updateSessionRequest,
    updateSessionSuccess,
    updateSessionFailure,
    deleteSessionRequest,
    deleteSessionSuccess,
    deleteSessionFailure,

    // Session Control
    pauseSessionRequest,
    pauseSessionSuccess,
    pauseSessionFailure,
    resumeSessionRequest,
    resumeSessionSuccess,
    resumeSessionFailure,
    completeSessionRequest,
    completeSessionSuccess,
    completeSessionFailure,
    abandonSessionRequest,
    abandonSessionSuccess,
    abandonSessionFailure,

    // Active Sessions
    fetchActiveSessionsRequest,
    fetchActiveSessionsSuccess,
    fetchActiveSessionsFailure,

    // Activities
    fetchSessionActivitiesRequest,
    fetchSessionActivitiesSuccess,
    fetchSessionActivitiesFailure,
    createSessionActivityRequest,
    createSessionActivitySuccess,
    createSessionActivityFailure,

    // Bookmarks
    fetchSessionBookmarksRequest,
    fetchSessionBookmarksSuccess,
    fetchSessionBookmarksFailure,
    createSessionBookmarkRequest,
    createSessionBookmarkSuccess,
    createSessionBookmarkFailure,

    // Templates
    fetchSessionTemplatesRequest,
    fetchSessionTemplatesSuccess,
    fetchSessionTemplatesFailure,
    createSessionFromTemplateRequest,
    createSessionFromTemplateSuccess,
    createSessionFromTemplateFailure,

    // Stats
    fetchSessionStatsRequest,
    fetchSessionStatsSuccess,
    fetchSessionStatsFailure,

    // Filters
    fetchSessionsByTypeRequest,
    fetchSessionsByTypeSuccess,
    fetchSessionsByTypeFailure,
    fetchSessionsByDateRangeRequest,
    fetchSessionsByDateRangeSuccess,
    fetchSessionsByDateRangeFailure,

    // Analytics
    fetchSessionAnalyticsRequest,
    fetchSessionAnalyticsSuccess,
    fetchSessionAnalyticsFailure,

    // Utility
    clearCurrentSession,
    clearSessionError,
} = sessionSlice.actions;

export default sessionSlice.reducer;
